import { useEffect, useState } from 'react'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { FileKind, type FileInfo } from '../../models/File'
import { useAppDispatch } from '../../store/hooks'
import { useLazyDownloadGlobalFileRequestQuery } from '../../store/media/file'
import { useDeleteFileMutation } from '../../store/media/media'
import { useGetUserByIdQuery, useSaveUserByIdMutation, userApi } from '../../store/user/user'
import { getUserInitials } from '../../views/utils'
import { ImageUpload } from '../ImageUpload/ImageUpload'
import { type ImageUploadLabels, type UploadProgressCallback } from '../ImageUpload/models'
import { useAssetFileUpload } from '../ImageUpload/useAssetFileUpload'
import { AVATAR_MAX_DIMENSION } from '../ImageUpload/utils'

const MB = 1024 * 1024
const MAX_SIZE = 5 * MB
const ACCEPTED_TYPES = ['image/gif', 'image/jpeg', 'image/png', 'image/webp']

const LABELS: ImageUploadLabels = {
  drop: 'Drop a new photo here',
  hint: 'max 5 MB',
  change: 'Change photo',
  remove: 'Remove',
  removing: 'Deleting file...',
  removeFailed: 'Could not remove photo',
  wrongFormat: 'Only JPG, PNG, GIF, WEBP files are accepted.',
  tooLarge: 'Please upload an image under 5 MB.',
  cropTitle: 'Adjust photo',
  cropSave: 'Save photo',
}

type UserAvatarUploadProps = {
  accountId: string
  userId: string
}

export const UserAvatarUpload: React.FC<UserAvatarUploadProps> = ({ accountId, userId }) => {
  const userQuery = useGetUserByIdQuery({ accountId, userId })
  const userData = userQuery.data?.data
  const settings = userData?.settings ?? {}
  const initials = getUserInitials(userData)
  const avatar = settings.assets?.avatar?.avatarFile ?? null
  const avatarFileId = avatar?.fileId

  const [resolved, setResolved] = useState<{ fileId: string; url: string } | null>(null)

  const [downloadReq, { isLoading: isLazyDownload }] = useLazyDownloadGlobalFileRequestQuery()
  const uploadAsset = useAssetFileUpload(accountId, FileKind.USER_AVATAR_ASSET)
  const [deleteFile] = useDeleteFileMutation()
  const [saveUser] = useSaveUserByIdMutation()
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()

  const imageUrl = resolved && resolved.fileId === avatarFileId ? resolved.url : null

  const patchAvatar = (file: FileInfo | null) => {
    dispatch(
      userApi.util.updateQueryData('getUserById', { accountId, userId }, (draft) => {
        draft.data.settings = { ...draft.data.settings, assets: { avatar: file ? { avatarFile: file } : null } }
      }),
    )
  }

  useEffect(() => {
    if (!avatarFileId) {
      return
    }
    let active = true
    downloadReq({ accountId, fileId: avatarFileId })
      .unwrap()
      .then(({ data }) => {
        if (active) {
          setResolved({ fileId: avatarFileId, url: data.location })
        }
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [accountId, avatarFileId, downloadReq])

  const handleUpload = async (file: File, onProgress: UploadProgressCallback) => {
    const previousFileId = avatar?.fileId ?? null
    const fileInfo = await uploadAsset(file, onProgress)
    await saveUser({
      accountId,
      userId,
      userData: { settings: { ...settings, assets: { avatar: { avatarFile: fileInfo } } } },
    }).unwrap()
    patchAvatar(fileInfo)
    if (previousFileId && previousFileId !== fileInfo.fileId) {
      deleteFile({ accountId, fileId: previousFileId })
        .unwrap()
        .catch(() => undefined)
    }
  }

  const handleRemove = async () => {
    const fileId = avatar?.fileId
    if (fileId == null) {
      return
    }
    await saveUser({
      accountId,
      userId,
      userData: { settings: { ...settings, assets: { avatar: null } } },
      keepEmptyItems: true,
    }).unwrap()
    await deleteFile({ accountId, fileId })
      .unwrap()
      .catch(() => undefined)
    patchAvatar(null)
  }

  if (userQuery.isLoading) {
    return 'Loading...'
  }

  return (
    <ImageUpload
      imageUrl={imageUrl}
      shape='circle'
      placeholder={initials}
      acceptedTypes={ACCEPTED_TYPES}
      maxSize={MAX_SIZE}
      maxDimension={AVATAR_MAX_DIMENSION}
      labels={LABELS}
      loading={isLazyDownload}
      onUpload={handleUpload}
      onRemove={handleRemove}
      onUploadResult={(isSuccess) => trackingClient.track({ eventName: TrackEvent.PROFILE_PHOTO_UPLOAD, eventDetails: { isSuccess } })}
    />
  )
}
